import { canModerate } from '@cult/domain';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { InlineError, PageContainer } from '../../components';
import { PraiseForm, usePraise } from '../../features/praises';
import { FORM_ORIGINS, type FormOrigin } from '../../lib/form-origins';
import { useCurrentUser } from '../../state/use-current-user';

type Search = {
  origin?: FormOrigin;
};

const EditPraisePage = () => {
  const { praiseId } = Route.useParams();
  const { origin = FORM_ORIGINS.FEED } = Route.useSearch();
  const praiseQuery = usePraise(praiseId);
  const user = useCurrentUser((s) => s.user);

  if (praiseQuery.isPending) {
    return (
      <PageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PageContainer>
    );
  }

  if (praiseQuery.isError) {
    return (
      <PageContainer>
        <InlineError title="Couldn't load praise" detail={praiseQuery.error.message} />
      </PageContainer>
    );
  }

  const praise = praiseQuery.data;
  // Same rule as the API: the author or a moderator (admins at any time). The form handles the closed edit window.
  const canEdit = user !== null && (praise.author.id === user.id || canModerate(user.role));
  if (!canEdit) {
    return (
      <PageContainer>
        <InlineError title="You can't edit this praise" detail="Only its author can change it." />
      </PageContainer>
    );
  }

  return <PraiseForm mode="edit" origin={origin} praise={praise} />;
};

export const Route = createFileRoute('/_auth/praises/$praiseId/edit')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    origin: search.origin === FORM_ORIGINS.ADMIN ? FORM_ORIGINS.ADMIN : undefined,
  }),
  component: EditPraisePage,
});
